import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { SnackType } from '@/interfaces/snack'
import { ApolloResponse } from '@/interfaces/apollo'
import { Producer } from '@/modules/Producer/interfaces/producer'
import { showErrors, showNotification } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import ProducerDialog from '@/modules/Producer/scripts/producer-dialog'
import { getProducers, editProducer } from '@/modules/Producer/gql/producer.gql'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh
  }
})
export default class ProducerList extends mixins(ProducerDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Setting'
    },
    {
      text: 'Source'
    }
  ]
  searchVal = ''
  producers: Producer[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME'
  }

  renaming = false
  nullProducerName = false
  renameProducerName = ''

  tableColumn = [
    { field: 'name', title: 'Source', sortable: true, slots: { default: 'name_default' } },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 112 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  mounted () {
    this.getProducers()
  }

  async getProducers () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getProducers,
        variables: {
          filter: {
            name: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.producers = result.data.getProducers.producers
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getProducers.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  renameProducer (e: Event, item: any) {
    e.preventDefault()
    e.stopPropagation()
    this.targetProducer = Object.assign({}, item)
    this.renameProducerName = item.name
    this.renaming = true
  }

  stopPreventDefault (e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  async renameConfirm (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.nullProducerName = !this.renameProducerName
    if (this.nullProducerName) {
      return
    }
    await this.renameProducerNameConfirm()
    this.renaming = false
  }

  async renameCancel (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.renaming = false
    this.renameProducerName = ''
  }

  async renameProducerNameConfirm () {
    if (!this.targetProducer) {
      return
    }
    const { id } = this.targetProducer
    try {
      await this.$apollo.mutate({
        mutation: editProducer,
        variables: {
          producerId: Number(id),
          producerInput: {
            name: this.renameProducerName
          }
        }
      })
      showNotification({
        msg: 'Update the producer name successfully',
        type: SnackType.Success
      })
      this.getProducers()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  refreshList () {
    this.searchVal = ''
    this.getProducers()
  }

  async deleteProducerConfirm () {
    await this.deleteProducerPost()
    this.getProducers()
  }

  routeToDetail (item: any) {
    return {
      name: 'ProducerDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getProducers()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getProducers()
  }
}
